import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  startBankReconciliationImport,
  getBankReconciliationImport,
  getBankReconciliationImportSuggestions,
  retryBankReconciliationImportPart,
  BankReconciliationImportResponse,
  BankReconciliationImportPart,
  BankReconciliationProgressiveSuggestion,
  BankReconciliationRun,
  BankReconciliationRunListItem,
  BankReconciliationSuggestion,
  BankReconciliationSuggestionEditRequest,
  editBankReconciliationSuggestion,
  executeBankReconciliationBatch,
  executeBankReconciliationSuggestion,
  getBankReconciliationRun,
  refreshBankReconciliationReceipts,
  refreshBankReconciliationSuggestionReceipt,
  listBankReconciliationRuns,
  BankReconciliationDeletion,
  deleteBankReconciliationImport,
  deleteBankReconciliationRun,
  getBankReconciliationDeletion,
  retryBankReconciliationDeletion,
} from '../api/client';
import EditSuggestionModal from '../components/reconciliation/EditSuggestionModal';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import RunHistory from '../components/reconciliation/RunHistory';
import RunSummary from '../components/reconciliation/RunSummary';
import SuggestionCard from '../components/reconciliation/SuggestionCard';
import UploadPanel from '../components/reconciliation/UploadPanel';
import ImportProgress from '../components/reconciliation/ImportProgress';
import DeleteAnalysisModal from '../components/reconciliation/DeleteAnalysisModal';
import { useAuth } from '../context/AuthContext';
import {
  isSuggestionExecutable,
  matchesReconciliationFilter,
  ReconciliationFilter,
} from '../components/reconciliation/utils';

function errorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: string | { message?: string } } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

export default function Reconciliation() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const scopeRef = useRef(tenantId);
  scopeRef.current = tenantId;
  const [importId, setImportId] = useState<string | null>(null);
  const [importData, setImportData] = useState<BankReconciliationImportResponse | null>(null);
  const [liveSuggestions, setLiveSuggestions] = useState<BankReconciliationProgressiveSuggestion[]>([]);
  const liveSuggestionsRef = useRef<BankReconciliationProgressiveSuggestion[]>([]);
  liveSuggestionsRef.current = liveSuggestions;
  const [partStart, setPartStart] = useState(1);
  const [pollGeneration, setPollGeneration] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const activeImportRef = useRef(importId);
  activeImportRef.current = importId;
  const runRequestRef = useRef(0);
  const [runs, setRuns] = useState<BankReconciliationRunListItem[]>([]);
  const [run, setRun] = useState<BankReconciliationRun | null>(null);
  const activeRunRef = useRef<string | undefined>();
  activeRunRef.current = run?.run_id;
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingRun, setLoadingRun] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [executingIds, setExecutingIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [overrideReasons, setOverrideReasons] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{
    suggestion: BankReconciliationSuggestion;
    runId: string;
    live: boolean;
  } | null>(null);
  const [customerDetails, setCustomerDetails] = useState<{
    customerId: number;
    displayName: string;
  } | null>(null);
  const [filter, setFilter] = useState<ReconciliationFilter>('all');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [warning, setWarning] = useState('');
  const [refreshingReceipts, setRefreshingReceipts] = useState(false);
  const [receiptRefreshError, setReceiptRefreshError] = useState('');
  const [refreshingSuggestionReceipts, setRefreshingSuggestionReceipts] = useState<Set<string>>(new Set());
  const [suggestionReceiptFeedback, setSuggestionReceiptFeedback] = useState<Record<string, {
    state: 'checking' | 'not_found' | 'possible_match' | 'error';
    error?: string;
  }>>({});
  const receiptRefreshes = useRef(new Set<string>());
  const epochRef = useRef(0);
  const [runCanDelete, setRunCanDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string; kind: 'import' | 'run'; version: number; filename: string; wholeImport: boolean;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deletionId, setDeletionId] = useState<string | null>(null);
  const [deletionData, setDeletionData] = useState<BankReconciliationDeletion | null>(null);
  const [deletionPoll, setDeletionPoll] = useState(0);
  const openCustomerDetails = (customerId: string, displayName: string) => {
    const numericId = Number(customerId);
    if (Number.isSafeInteger(numericId) && numericId > 0) {
      setCustomerDetails({ customerId: numericId, displayName });
    }
  };

  const loadHistory = useCallback(async (selectFirst = false) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    const request = runRequestRef.current;
    setLoadingHistory(true);
    try {
      const response = await listBankReconciliationRuns({ limit: 25 });
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setRuns(response.data.runs);
      if (selectFirst && response.data.runs[0] && request === runRequestRef.current) {
        setLoadingRun(true);
        const detail = await getBankReconciliationRun(response.data.runs[0].run_id);
        if (scopeRef.current !== scope || epochRef.current !== epoch || request !== runRequestRef.current) return;
        setRun(detail.data.run);
        setRunCanDelete(detail.data.can_delete);
      }
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setError(errorMessage(err, t('reconciliation.errors.history')));
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) {
        setLoadingHistory(false);
        if (selectFirst && request === runRequestRef.current) setLoadingRun(false);
      }
    }
  }, [run, t, tenantId]);

  useEffect(() => {
    epochRef.current += 1;
    runRequestRef.current += 1;
    setRun(null);
    setRuns([]);
    setSelected(new Set());
    setBusyIds(new Set());
    setExecutingIds(new Set());
    setOverrideReasons({});
    setEditing(null);
    setAnalyzing(false);
    setRetrying(false);
    setImportData(null);
    setLiveSuggestions([]);
    setPartStart(1);
    setError('');
    setNotice('');
    setWarning('');
    setRefreshingReceipts(false);
    setReceiptRefreshError('');
    setRefreshingSuggestionReceipts(new Set());
    setSuggestionReceiptFeedback({});
    receiptRefreshes.current.clear();
    setRunCanDelete(false);
    setDeleteTarget(null);
    setDeleting(false);
    setDeletionData(null);
    setDeletionId(tenantId ? localStorage.getItem(`reconciliation-deletion:${tenantId}`) : null);
    setImportId(tenantId ? localStorage.getItem(`reconciliation-import:${tenantId}`) : null);
    void loadHistory(true);
    // Initial load only; subsequent updates explicitly refresh history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadProgressiveSuggestions = useCallback(async (
    id: string,
    analyzedPayments: number,
    refreshAll = false,
  ) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    let merged = refreshAll ? [] : [...liveSuggestionsRef.current];
    if (!refreshAll && merged.length >= analyzedPayments) return;
    const present = new Set(merged.map((item) => item.source_position));
    let start = 1;
    while (present.has(start)) start += 1;
    while (true) {
      const response = await getBankReconciliationImportSuggestions(id, start, 100);
      if (scopeRef.current !== scope || epochRef.current !== epoch || activeImportRef.current !== id) return;
      const byPosition = new Map(merged.map((item) => [item.source_position, item]));
      response.data.suggestions.forEach((item) => byPosition.set(item.source_position, item));
      merged = [...byPosition.values()].sort((a, b) => a.source_position - b.source_position);
      setLiveSuggestions(merged);
      if (!response.data.next_start || response.data.next_start <= start) break;
      start = response.data.next_start;
    }
  }, [tenantId]);

  const refreshSavedReceipts = useCallback(async (target: BankReconciliationRun, force = false) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    const request = runRequestRef.current;
    const key = `${scope}:${epoch}:${request}:${target.import_id ?? target.run_id}`;
    if (!force && receiptRefreshes.current.has(key)) return;
    receiptRefreshes.current.add(key);
    setRefreshingReceipts(true);
    setReceiptRefreshError('');
    setRefreshingSuggestionReceipts(new Set());
    setSuggestionReceiptFeedback({});
    try {
      const response = await refreshBankReconciliationReceipts(target.run_id, target.version);
      if (scopeRef.current !== scope || epochRef.current !== epoch || runRequestRef.current !== request) return;
      if ((response.data.warnings ?? []).length > 0) {
        setWarning(t('reconciliation.customerAssociationWarning'));
      }
      setRun((current) => current?.run_id === target.run_id ? response.data.run : current);
      if (target.import_id && activeImportRef.current === target.import_id) {
        await loadProgressiveSuggestions(target.import_id, 0, true);
      }
      if (scopeRef.current !== scope || epochRef.current !== epoch || runRequestRef.current !== request) return;
      setSelected(new Set());
      setEditing(null);
      await loadHistory();
    } catch (err) {
      if (scopeRef.current === scope && epochRef.current === epoch && runRequestRef.current === request) {
        setReceiptRefreshError(errorMessage(err, t('reconciliation.receiptResolution.refreshError')));
      }
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch && runRequestRef.current === request) setRefreshingReceipts(false);
    }
  }, [tenantId, loadHistory, loadProgressiveSuggestions, t]);

  useEffect(() => {
    if (run && run.status !== 'analyzing' && !deleting && !deletionId) void refreshSavedReceipts(run);
  }, [run, refreshSavedReceipts, deleting, deletionId]);

  useEffect(() => {
    const first = liveSuggestions[0];
    if (run || !first || !importData || ['admitting', 'queued', 'analyzing'].includes(importData.import_record.status)
        || deleting || deletionId) return;
    const epoch = epochRef.current;
    const request = runRequestRef.current;
    const key = `${tenantId}:${epoch}:${request}:${importData.import_record.import_id}`;
    if (receiptRefreshes.current.has(key)) return;
    receiptRefreshes.current.add(key);
    void getBankReconciliationRun(first.run_id).then(response => {
      if (epochRef.current === epoch && runRequestRef.current === request) void refreshSavedReceipts(response.data.run, true);
    }).catch(err => {
      if (epochRef.current === epoch && runRequestRef.current === request) {
        setReceiptRefreshError(errorMessage(err, t('reconciliation.receiptResolution.refreshError')));
      }
    });
  }, [run, liveSuggestions, importData, tenantId, refreshSavedReceipts, deleting, deletionId, t]);

  useEffect(() => {
    if (!importId || !tenantId || deletionId || deleting) return;
    const scope = tenantId;
    const epoch = epochRef.current;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const poll = async () => {
      try {
        const response = await getBankReconciliationImport(importId, partStart);
        if (stopped || scopeRef.current !== scope || epochRef.current !== epoch) return;
        failures = 0;
        setImportData(response.data);
        const terminal = !['admitting', 'queued', 'analyzing'].includes(
          response.data.import_record.status,
        );
        await loadProgressiveSuggestions(
          importId,
          response.data.import_record.processed_payments,
          terminal,
        );
        if (stopped || scopeRef.current !== scope || epochRef.current !== epoch) return;
        if (!terminal) {
          timer = setTimeout(() => void poll(), 5000);
        } else {
          void loadHistory();
        }
      } catch (err) {
        if (stopped || scopeRef.current !== scope || epochRef.current !== epoch) return;
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          const id = (err as { response?: { data?: { detail?: { deletion_id?: string } } } })
            ?.response?.data?.detail?.deletion_id;
          clearAnalysis();
          if (id) trackDeletion(id);
          else if (localStorage.getItem(`reconciliation-import:${scope}`) === importId) {
            localStorage.removeItem(`reconciliation-import:${scope}`);
          }
          setNotice(t('reconciliation.deletion.gone'));
          void loadHistory();
          return;
        }
        setError(errorMessage(err, t('reconciliation.import.statusError')));
        failures += 1;
        if (failures < 3) timer = setTimeout(() => void poll(), 5000);
      }
    };
    void poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // Run selection/history changes must not restart background-status polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId, tenantId, partStart, pollGeneration, t, loadProgressiveSuggestions, deletionId, deleting]);

  const clearAnalysis = () => {
    epochRef.current += 1;
    runRequestRef.current += 1;
    activeImportRef.current = null;
    activeRunRef.current = undefined;
    liveSuggestionsRef.current = [];
    setImportId(null);
    setImportData(null);
    setLiveSuggestions([]);
    setRun(null);
    setRunCanDelete(false);
    setSelected(new Set());
    setBusyIds(new Set());
    setExecutingIds(new Set());
    setOverrideReasons({});
    setEditing(null);
    setPartStart(1);
    setLoadingRun(false);
    setLoadingHistory(false);
    setRetrying(false);
    setAnalyzing(false);
    setRefreshingReceipts(false);
    setReceiptRefreshError('');
    setRefreshingSuggestionReceipts(new Set());
    setSuggestionReceiptFeedback({});
    receiptRefreshes.current.clear();
  };

  const trackDeletion = (id: string) => {
    if (tenantId) localStorage.setItem(`reconciliation-deletion:${tenantId}`, id);
    setDeletionId(id);
    setDeleteTarget(null);
    clearAnalysis();
  };

  useEffect(() => {
    if (!deletionId || !tenantId) return;
    const scope = tenantId;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const finish = (kind: 'import' | 'run', owner: string) => {
      if (kind === 'import' && localStorage.getItem(`reconciliation-import:${scope}`) === owner) {
        localStorage.removeItem(`reconciliation-import:${scope}`);
      }
      if (localStorage.getItem(`reconciliation-deletion:${scope}`) === deletionId) {
        localStorage.removeItem(`reconciliation-deletion:${scope}`);
      }
      clearAnalysis();
      setDeletionId(null);
      setDeletionData(null);
      setError('');
      void loadHistory();
    };
    const poll = async () => {
      try {
        const response = await getBankReconciliationDeletion(deletionId);
        if (stopped || scopeRef.current !== scope) return;
        failures = 0;
        setDeletionData(response.data);
        if (response.data.status === 'completed') {
          finish(response.data.target_kind, response.data.target_id);
          setNotice(t('reconciliation.deletion.completed'));
        } else if (response.data.status !== 'failed') {
          timer = setTimeout(() => void poll(), 2000);
        }
      } catch (err) {
        if (stopped || scopeRef.current !== scope) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 404) {
          finish(deletionId.startsWith('imp_') ? 'import' : 'run', deletionId);
          setNotice(t('reconciliation.deletion.gone'));
          return;
        }
        setError(errorMessage(err, t('reconciliation.deletion.statusError')));
        if (++failures < 3) timer = setTimeout(() => void poll(), 5000);
      }
    };
    void poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // Scope and deletion identity own this polling lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletionId, tenantId, deletionPoll, t]);

  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === `reconciliation-deletion:${tenantId}` && event.newValue &&
          [activeImportRef.current, activeRunRef.current].includes(event.newValue)) {
        trackDeletion(event.newValue);
      }
      if (event.key === `reconciliation-import:${tenantId}` && event.oldValue &&
          !event.newValue && event.oldValue === activeImportRef.current) {
        clearAnalysis();
        setNotice(t('reconciliation.deletion.gone'));
        void loadHistory();
      }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, t]);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const scope = tenantId;
    const epoch = ++epochRef.current;
    runRequestRef.current += 1;
    setDeleting(true);
    setError('');
    try {
      const response = await (deleteTarget.kind === 'import'
        ? deleteBankReconciliationImport(deleteTarget.id, deleteTarget.version)
        : deleteBankReconciliationRun(deleteTarget.id, deleteTarget.version));
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setDeletionData(response.data);
      trackDeletion(response.data.deletion_id);
      setRuns((current) => current.filter((item) => response.data.target_kind === 'import'
        ? item.import_id !== response.data.target_id : item.run_id !== response.data.target_id));
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      const savedId = (err as { response?: { data?: { detail?: { deletion_id?: string } } } })
        ?.response?.data?.detail?.deletion_id;
      if (savedId) trackDeletion(savedId);
      setError(errorMessage(err, t('reconciliation.deletion.error')));
    } finally {
      if (scopeRef.current === scope) {
        setDeleting(false);
        setLoadingRun(false);
        setLoadingHistory(false);
      }
    }
  };

  const retryDeletion = async () => {
    if (!deletionData || deleting) return;
    const scope = tenantId;
    setDeleting(true);
    setError('');
    try {
      const response = await retryBankReconciliationDeletion(deletionData.deletion_id, deletionData.version);
      if (scopeRef.current === scope) {
        setDeletionData(response.data);
        setDeletionPoll((value) => value + 1);
      }
    } catch (err) {
      if (scopeRef.current === scope) {
        setError(errorMessage(err, t('reconciliation.deletion.error')));
        setDeletionPoll((value) => value + 1);
      }
    } finally {
      if (scopeRef.current === scope) setDeleting(false);
    }
  };

  const rememberImport = (id: string, showLive = true) => {
    if (tenantId) localStorage.setItem(`reconciliation-import:${tenantId}`, id);
    if (activeImportRef.current !== id) setLiveSuggestions([]);
    if (showLive) setRun(null);
    setImportId(id);
    setPartStart(1);
    setPollGeneration((value) => value + 1);
  };

  const selectRun = async (runId: string) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    const request = ++runRequestRef.current;
    setRefreshingReceipts(false);
    setReceiptRefreshError('');
    setRefreshingSuggestionReceipts(new Set());
    setSuggestionReceiptFeedback({});
    setLoadingRun(true);
    setError('');
    setNotice('');
    setWarning('');
    setSelected(new Set());
    setEditing(null);
    try {
      const response = await getBankReconciliationRun(runId);
      if (scopeRef.current !== scope || epochRef.current !== epoch || request !== runRequestRef.current) return;
      setRun(response.data.run);
      setRunCanDelete(response.data.can_delete);
      if (response.data.run.import_id && response.data.run.import_id !== activeImportRef.current) {
        rememberImport(response.data.run.import_id, false);
      }
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch || request !== runRequestRef.current) return;
      setError(errorMessage(err, t('reconciliation.errors.run')));
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch && request === runRequestRef.current) setLoadingRun(false);
    }
  };

  const analyze = async (file: File) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    setAnalyzing(true);
    setError('');
    setNotice('');
    setWarning('');
    try {
      const response = await startBankReconciliationImport(file);
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      rememberImport(response.data.import_record.import_id);
      setImportData(response.data);
      setSelected(new Set());
      setNotice(t(response.data.reused ? 'reconciliation.import.reused' : 'reconciliation.import.accepted'));
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      const savedId = (err as { response?: { data?: { detail?: { import_id?: string } } } })
        ?.response?.data?.detail?.import_id;
      if (savedId) rememberImport(savedId);
      setError(errorMessage(err, t('reconciliation.errors.analyze')));
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) setAnalyzing(false);
    }
  };

  const retryPart = async (part: BankReconciliationImportPart) => {
    if (!importId) return;
    const scope = tenantId;
    const epoch = epochRef.current;
    const id = importId;
    setRetrying(true);
    setError('');
    try {
      await retryBankReconciliationImportPart(id, part.part_number, part.version);
      if (scopeRef.current === scope && epochRef.current === epoch && activeImportRef.current === id) {
        setPollGeneration((value) => value + 1);
      }
    } catch (err) {
      if (scopeRef.current === scope && epochRef.current === epoch && activeImportRef.current === id) {
        setError(errorMessage(err, t('reconciliation.import.retryError')));
        setPollGeneration((value) => value + 1);
      }
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) setRetrying(false);
    }
  };

  const replaceSuggestion = (suggestion: BankReconciliationSuggestion, runVersion?: number) => {
    setRun((current) => current && current.run_id === run?.run_id ? {
      ...current,
      version: runVersion ?? current.version,
      suggestions: current.suggestions.map((item) =>
        item.suggestion_id === suggestion.suggestion_id ? suggestion : item),
    } : current);
  };

  const refreshTargetRun = async (runId: string, live: boolean) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    let response;
    try {
      response = await getBankReconciliationRun(runId);
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      if ((err as { response?: { status?: number } })?.response?.status === 404) {
        const owner = live ? importId : run?.import_id;
        if (owner && localStorage.getItem(`reconciliation-import:${scope}`) === owner) {
          localStorage.removeItem(`reconciliation-import:${scope}`);
        }
        clearAnalysis();
        setNotice(t('reconciliation.deletion.gone'));
        void loadHistory();
        return;
      }
      throw err;
    }
    if (scopeRef.current !== scope || epochRef.current !== epoch) return;
    if (live) {
      const suggestions = new Map(
        response.data.run.suggestions.map((item) => [item.suggestion_id, item]),
      );
      setLiveSuggestions((current) => current.map((item) => (
        item.run_id === runId && suggestions.has(item.suggestion.suggestion_id)
          ? {
            ...item,
            run_version: response.data.run.version,
            run_status: response.data.run.status,
            suggestion: suggestions.get(item.suggestion.suggestion_id)!,
          }
          : item
      )));
    } else if (activeRunRef.current === runId) {
      setRun(response.data.run);
      setRunCanDelete(response.data.can_delete);
    }
    await loadHistory();
  };

  const refreshRun = async () => {
    const epoch = epochRef.current;
    try {
      if (run) await refreshTargetRun(run.run_id, false);
    } catch (err) {
      if (epochRef.current === epoch) setError(errorMessage(err, t('reconciliation.errors.run')));
    }
  };

  const refreshSuggestionReceipt = async (
    runId: string,
    runVersion: number,
    suggestion: BankReconciliationSuggestion,
    live: boolean,
  ) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    const key = `${runId}:${suggestion.suggestion_id}`;
    setRefreshingSuggestionReceipts((current) => new Set(current).add(key));
    setSuggestionReceiptFeedback((current) => ({
      ...current,
      [key]: { state: 'checking' },
    }));
    setWarning('');
    try {
      const response = await refreshBankReconciliationSuggestionReceipt(
        runId,
        suggestion.suggestion_id,
        runVersion,
        suggestion.version,
      );
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      if ((response.data.warnings ?? []).length > 0) {
        setWarning(t('reconciliation.customerAssociationWarning'));
      }
      if (live) {
        if (response.data.run.import_id && activeImportRef.current === response.data.run.import_id) {
          await loadProgressiveSuggestions(response.data.run.import_id, 0, true);
        } else {
          const refreshed = new Map(
            response.data.run.suggestions.map((item) => [item.suggestion_id, item]),
          );
          setLiveSuggestions((current) => current.map((item) => (
            item.run_id === runId && refreshed.has(item.suggestion.suggestion_id)
              ? {
                ...item,
                run_version: response.data.run.version,
                run_status: response.data.run.status,
                suggestion: refreshed.get(item.suggestion.suggestion_id)!,
              }
              : item
          )));
        }
      } else if (activeRunRef.current === runId) {
        setRun(response.data.run);
        setRunCanDelete(response.data.can_delete);
      }
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setSuggestionReceiptFeedback((current) => {
        const next = { ...current };
        if (response.data.result === 'not_found' || response.data.result === 'possible_match') {
          next[key] = { state: response.data.result };
        } else {
          delete next[key];
        }
        return next;
      });
      await loadHistory();
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setSuggestionReceiptFeedback((current) => ({
        ...current,
        [key]: {
          state: 'error',
          error: errorMessage(err, t('reconciliation.receiptResolution.refreshError')),
        },
      }));
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) {
        setRefreshingSuggestionReceipts((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    }
  };

  const editSuggestion = async (
    runId: string,
    suggestion: BankReconciliationSuggestion,
    request: BankReconciliationSuggestionEditRequest,
    live = false,
  ) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    setBusyIds((current) => new Set(current).add(suggestion.suggestion_id));
    setSuggestionReceiptFeedback((current) => {
      const next = { ...current };
      delete next[`${runId}:${suggestion.suggestion_id}`];
      return next;
    });
    setError('');
    setWarning('');
    try {
      const response = await editBankReconciliationSuggestion(runId, suggestion.suggestion_id, request);
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      if (live) {
        setLiveSuggestions((current) => current.map((item) => (
          item.run_id === runId && item.suggestion.suggestion_id === suggestion.suggestion_id
            ? { ...item, run_version: response.data.run_version, suggestion: response.data.suggestion }
            : item
        )));
      } else {
        replaceSuggestion(response.data.suggestion, response.data.run_version);
      }
      setEditing(null);
      if ((response.data.warnings ?? []).length > 0) {
        setWarning(t('reconciliation.customerAssociationWarning'));
      }
      setSelected((current) => {
        const next = new Set(current);
        next.delete(suggestion.suggestion_id);
        return next;
      });
      await refreshTargetRun(runId, live);
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setError(errorMessage(err, t('reconciliation.errors.edit')));
      if (live) await refreshTargetRun(runId, true).catch(() => undefined);
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) setBusyIds((current) => {
        const next = new Set(current);
        next.delete(suggestion.suggestion_id);
        return next;
      });
    }
  };

  const executeOne = async (
    runId: string,
    suggestion: BankReconciliationSuggestion,
    live = false,
  ) => {
    const scope = tenantId;
    const epoch = epochRef.current;
    const reason = overrideReasons[suggestion.suggestion_id]?.trim();
    const override = !!suggestion.receipt_matches?.length || (suggestion.requires_override && !suggestion.material_edit_made);
    setBusyIds((current) => new Set(current).add(suggestion.suggestion_id));
    setExecutingIds((current) => new Set(current).add(suggestion.suggestion_id));
    setError('');
    setWarning('');
    try {
      const response = await executeBankReconciliationSuggestion(runId, suggestion.suggestion_id, {
        version: suggestion.version,
        override,
        override_reason: override ? reason : undefined,
      });
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      if ((response.data.warnings ?? []).length > 0) {
        setWarning(t('reconciliation.customerAssociationWarning'));
      }
      await refreshTargetRun(runId, live);
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setSelected((current) => {
        const next = new Set(current);
        next.delete(suggestion.suggestion_id);
        return next;
      });
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setError(errorMessage(err, t('reconciliation.errors.execute')));
      await refreshTargetRun(runId, live).catch(() => undefined);
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) {
        setBusyIds((current) => {
          const next = new Set(current);
          next.delete(suggestion.suggestion_id);
          return next;
        });
        setExecutingIds((current) => {
          const next = new Set(current);
          next.delete(suggestion.suggestion_id);
          return next;
        });
      }
    }
  };

  const selectedSuggestions = useMemo(
    () => run?.suggestions.filter((item) => selected.has(item.suggestion_id) && isSuggestionExecutable(item)) ?? [],
    [run, selected],
  );
  const batchMissingReasons = selectedSuggestions.some((item) =>
    (!!item.receipt_matches?.length || (item.requires_override && !item.material_edit_made))
    && (overrideReasons[item.suggestion_id]?.trim().length ?? 0) < 3);

  const executeSelected = async () => {
    if (!run || refreshingReceipts || selectedSuggestions.length === 0 || batchMissingReasons) return;
    const scope = tenantId;
    const epoch = epochRef.current;
    const ids = selectedSuggestions.map((item) => item.suggestion_id);
    setBusyIds((current) => new Set([...current, ...ids]));
    setExecutingIds((current) => new Set([...current, ...ids]));
    setError('');
    setWarning('');
    try {
      const response = await executeBankReconciliationBatch(run.run_id, {
        run_version: run.version,
        suggestions: selectedSuggestions.map((item) => {
          const override = !!item.receipt_matches?.length || (item.requires_override && !item.material_edit_made);
          return {
            suggestion_id: item.suggestion_id,
            version: item.version,
            override,
            override_reason: override ? overrideReasons[item.suggestion_id]?.trim() : undefined,
          };
        }),
      });
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setNotice(t('reconciliation.batchComplete', {
        completed: response.data.completed_count,
        total: response.data.requested_count,
      }));
      if (response.data.results.some((item) => (item.warnings ?? []).length > 0)) {
        setWarning(t('reconciliation.customerAssociationWarning'));
      }
      setSelected(new Set());
      await refreshRun();
    } catch (err) {
      if (scopeRef.current !== scope || epochRef.current !== epoch) return;
      setError(errorMessage(err, t('reconciliation.errors.batch')));
      await refreshRun().catch(() => undefined);
    } finally {
      if (scopeRef.current === scope && epochRef.current === epoch) {
        setBusyIds((current) => {
          const next = new Set(current);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        setExecutingIds((current) => {
          const next = new Set(current);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }
    }
  };

  const filtered = run?.suggestions.filter((item) => matchesReconciliationFilter(item, filter)) ?? [];
  const liveFiltered = liveSuggestions.filter((item) =>
    matchesReconciliationFilter(item.suggestion, filter));
  const liveSummary = useMemo<BankReconciliationRun['summary']>(() => {
    const suggestions = liveSuggestions.map((item) => item.suggestion);
    const executed = suggestions.filter((item) =>
      ['completed', 'partial_success'].includes(item.execution?.status ?? ''));
    const matched = suggestions.reduce((sum, item) => sum + Number(item.allocated_amount), 0);
    const unmatched = suggestions.reduce((sum, item) => sum + Number(item.unallocated_amount), 0);
    return {
      imported_rows: suggestions.length,
      incoming_payment_rows: suggestions.length,
      ignored_debit_rows: 0,
      ignored_other_rows: 0,
      suggestion_count: suggestions.length,
      high_confidence_count: suggestions.filter((item) => item.confidence.level === 'high').length,
      medium_confidence_count: suggestions.filter((item) => item.confidence.level === 'medium').length,
      low_confidence_count: suggestions.filter((item) => item.confidence.level === 'low').length,
      executed_count: executed.length,
      resolved_count: suggestions.filter((item) => item.status === 'resolved' && !item.receipt_resolution?.stale).length,
      ignored_count: suggestions.filter((item) => item.status === 'ignored').length,
      failed_count: suggestions.filter((item) => ['failed', 'stale'].includes(item.status)).length,
      incoming_total: matched + unmatched,
      suggested_total: matched + unmatched,
      matched_total: matched,
      unmatched_total: unmatched,
      executed_total: executed.reduce((sum, item) => sum + Number(item.execution?.allocated_amount ?? 0), 0),
      currency: 'ILS',
    };
  }, [liveSuggestions]);
  const filters: ReconciliationFilter[] = ['all', 'high', 'medium', 'low', 'needs_review', 'executed'];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">{t('reconciliation.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('reconciliation.subtitle')}</p>
      </header>

      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {warning && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{warning}</div>}
      {notice && <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</div>}

      {deletionId && (
        <section aria-label={t('reconciliation.deletion.action')}
          className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-5">
          <p role="status" aria-live="polite">{t(`reconciliation.deletion.states.${deletionData?.status ?? 'queued'}`)}</p>
          <p className="text-sm">{t('reconciliation.deletion.progress', { count: deletionData?.deleted_records ?? 0 })}</p>
          {deletionData?.error_message && <p role="alert" className="text-sm text-red-700">{deletionData.error_message}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={deleting} onClick={() => setDeletionPoll((value) => value + 1)}
              className="rounded border bg-white px-3 py-2">{t('common.refresh')}</button>
            {deletionData?.status === 'failed' && <button type="button" disabled={deleting}
              onClick={() => void retryDeletion()} className="rounded border bg-white px-3 py-2">
              {t('reconciliation.deletion.retry')}
            </button>}
          </div>
        </section>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <UploadPanel analyzing={analyzing} disabled={deleting || !!deletionId} onAnalyze={analyze} />
        <RunHistory runs={runs} activeRunId={run?.run_id} loading={loadingHistory} onSelect={selectRun}
          disabled={deleting || !!deletionId} />
      </div>

      {importData && !deletionId && <ImportProgress data={importData} start={partStart} retrying={retrying}
        onPage={setPartStart} onRefresh={() => setPollGeneration((value) => value + 1)}
        onOpen={(id) => void selectRun(id)} onRetry={(part) => void retryPart(part)}
        onDelete={() => setDeleteTarget({ id: importData.import_record.import_id, kind: 'import',
          version: importData.import_record.version, filename: importData.import_record.original_filename,
          wholeImport: true })} />}

      {run && liveSuggestions.length > 0 && (
        <button type="button" onClick={() => {
          runRequestRef.current += 1;
          setRefreshingReceipts(false);
          setReceiptRefreshError('');
          setRun(null);
        }}
          className="rounded-lg border border-primary-300 bg-white px-4 py-2 text-sm font-medium text-primary-700">
          {t('reconciliation.progressive.showLive')}
        </button>
      )}

      {loadingRun && <div className="rounded-xl bg-white p-8 text-center text-gray-500">{t('common.loading')}</div>}
      {(run || liveSuggestions.length > 0) && !deletionId && (
        <div className="space-y-2">
          {refreshingReceipts && <p role="status">{t('reconciliation.receiptResolution.refreshing')}</p>}
          {receiptRefreshError && <p role="alert" className="text-sm text-red-700">{receiptRefreshError}</p>}
          <button type="button" disabled={refreshingReceipts || deleting || busyIds.size > 0}
            className="rounded-lg border bg-white px-3 py-2 text-sm"
            onClick={() => {
              if (run) void refreshSavedReceipts(run, true);
              else if (liveSuggestions[0]) {
                const epoch = epochRef.current;
                const request = runRequestRef.current;
                void getBankReconciliationRun(liveSuggestions[0].run_id).then(response => {
                  if (epochRef.current === epoch && runRequestRef.current === request) {
                    void refreshSavedReceipts(response.data.run, true);
                  }
                }).catch(err => {
                  if (epochRef.current === epoch && runRequestRef.current === request) {
                    setReceiptRefreshError(errorMessage(err, t('reconciliation.receiptResolution.refreshError')));
                  }
                });
              }
            }}>{t('reconciliation.receiptResolution.retry')}</button>
        </div>
      )}
      {!run && !deletionId && liveSuggestions.length > 0 && (
        <>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('reconciliation.progressive.title')}</h2>
            <p role="status" aria-live="polite" className="text-sm text-gray-500">
              {t('reconciliation.progressive.count', {
                count: liveSuggestions.length,
                total: importData?.import_record.total_payments ?? liveSuggestions.length,
              })}
            </p>
          </div>
          <RunSummary summary={liveSummary} />
          <section className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('reconciliation.filters.label')}>
              {filters.map((item) => (
                <button type="button" key={item} aria-pressed={filter === item}
                  onClick={() => setFilter(item)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium ${filter === item ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {t(`reconciliation.filters.${item}`)}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-blue-700">{t('reconciliation.progressive.batchLocked')}</p>
          </section>
          <div className="space-y-4">
            {liveFiltered.map((item) => (
              <SuggestionCard key={`${item.run_id}:${item.suggestion.suggestion_id}`}
                suggestion={item.suggestion} selectionEnabled={false}
                analyzing={item.run_status === 'analyzing'} selected={false}
                busy={refreshingReceipts || busyIds.has(item.suggestion.suggestion_id)
                  || refreshingSuggestionReceipts.has(`${item.run_id}:${item.suggestion.suggestion_id}`)}
                executing={executingIds.has(item.suggestion.suggestion_id)}
                overrideReason={overrideReasons[item.suggestion.suggestion_id] ?? ''}
                receiptRefreshState={suggestionReceiptFeedback[`${item.run_id}:${item.suggestion.suggestion_id}`]?.state}
                receiptRefreshError={suggestionReceiptFeedback[`${item.run_id}:${item.suggestion.suggestion_id}`]?.error}
                refreshingReceipt={refreshingSuggestionReceipts.has(`${item.run_id}:${item.suggestion.suggestion_id}`)}
                onSelectedChange={() => undefined}
                onOverrideReasonChange={(reason) => setOverrideReasons((current) => ({
                  ...current, [item.suggestion.suggestion_id]: reason,
                }))}
                onEdit={() => setEditing({ suggestion: item.suggestion, runId: item.run_id, live: true })}
                onIgnore={() => {
                  const reason = window.prompt(t('reconciliation.ignoreReasonPrompt'));
                  if (reason?.trim()) void editSuggestion(item.run_id, item.suggestion, {
                    version: item.suggestion.version, action: 'ignore', reason: reason.trim(),
                  }, true);
                }}
                onRestore={() => void editSuggestion(item.run_id, item.suggestion, {
                  version: item.suggestion.version, action: 'restore',
                }, true)}
                onExecute={() => void executeOne(item.run_id, item.suggestion, true)}
                onRefreshReceipt={() => void refreshSuggestionReceipt(
                  item.run_id, item.run_version, item.suggestion, true,
                )}
                onOpenCustomer={openCustomerDetails}
              />
            ))}
            {liveFiltered.length === 0 && <div className="rounded-xl bg-white p-8 text-center text-gray-500">{t('reconciliation.noSuggestions')}</div>}
          </div>
        </>
      )}
      {run && !loadingRun && !deletionId && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{run.original_filename}</h2>
              <p className="text-sm text-gray-500">{t('reconciliation.runProvider', { provider: run.provider })}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => void refreshRun()} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium">
                <RefreshCw className="h-4 w-4" aria-hidden="true" />{t('common.refresh')}
              </button>
              {runCanDelete && <button type="button" onClick={() => setDeleteTarget({
                id: run.run_id, kind: 'run', version: run.version, filename: run.original_filename,
                wholeImport: !!run.import_id,
              })} className="rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700">
                {t('reconciliation.deletion.action')}
              </button>}
            </div>
          </div>
          <RunSummary summary={run.summary} />
          <section className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('reconciliation.filters.label')}>
              {filters.map((item) => (
                <button
                  type="button"
                  key={item}
                  aria-pressed={filter === item}
                  onClick={() => setFilter(item)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium ${filter === item ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {t(`reconciliation.filters.${item}`)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => void executeSelected()}
                disabled={refreshingReceipts || selectedSuggestions.length === 0 || batchMissingReasons || busyIds.size > 0}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ltr:ml-auto rtl:mr-auto"
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                {t('reconciliation.executeSelected', { count: selectedSuggestions.length })}
              </button>
            </div>
            {batchMissingReasons && <p className="mt-2 text-xs text-amber-700">{t('reconciliation.batchReasonRequired')}</p>}
          </section>
          <div className="space-y-4">
            {filtered.map((suggestion) => (
              <SuggestionCard
                key={suggestion.suggestion_id}
                suggestion={suggestion}
                selected={selected.has(suggestion.suggestion_id)}
                busy={refreshingReceipts || busyIds.has(suggestion.suggestion_id)
                  || refreshingSuggestionReceipts.has(`${run.run_id}:${suggestion.suggestion_id}`)}
                executing={executingIds.has(suggestion.suggestion_id)}
                overrideReason={overrideReasons[suggestion.suggestion_id] ?? ''}
                receiptRefreshState={suggestionReceiptFeedback[`${run.run_id}:${suggestion.suggestion_id}`]?.state}
                receiptRefreshError={suggestionReceiptFeedback[`${run.run_id}:${suggestion.suggestion_id}`]?.error}
                refreshingReceipt={refreshingSuggestionReceipts.has(`${run.run_id}:${suggestion.suggestion_id}`)}
                onSelectedChange={(checked) => setSelected((current) => {
                  const next = new Set(current);
                  checked ? next.add(suggestion.suggestion_id) : next.delete(suggestion.suggestion_id);
                  return next;
                })}
                onOverrideReasonChange={(reason) => setOverrideReasons((current) => ({ ...current, [suggestion.suggestion_id]: reason }))}
                onEdit={() => setEditing({ suggestion, runId: run.run_id, live: false })}
                onIgnore={() => {
                  const reason = window.prompt(t('reconciliation.ignoreReasonPrompt'));
                  if (reason?.trim()) void editSuggestion(run.run_id, suggestion, { version: suggestion.version, action: 'ignore', reason: reason.trim() });
                }}
                onRestore={() => void editSuggestion(run.run_id, suggestion, { version: suggestion.version, action: 'restore' })}
                onExecute={() => void executeOne(run.run_id, suggestion)}
                onRefreshReceipt={() => void refreshSuggestionReceipt(
                  run.run_id, run.version, suggestion, false,
                )}
                onOpenCustomer={openCustomerDetails}
              />
            ))}
            {filtered.length === 0 && <div className="rounded-xl bg-white p-8 text-center text-gray-500">{t('reconciliation.noSuggestions')}</div>}
          </div>
        </>
      )}

      {editing && (
        <EditSuggestionModal
          runId={editing.runId}
          suggestion={editing.suggestion}
          saving={busyIds.has(editing.suggestion.suggestion_id)}
          onClose={() => setEditing(null)}
          onSave={(request) => void editSuggestion(editing.runId, editing.suggestion, request, editing.live)}
          onOpenCustomer={openCustomerDetails}
        />
      )}
      {customerDetails && (
        <CustomerDetailsModal
          customerId={customerDetails.customerId}
          displayName={customerDetails.displayName}
          onClose={() => setCustomerDetails(null)}
        />
      )}
      {deleteTarget && <DeleteAnalysisModal filename={deleteTarget.filename} wholeImport={deleteTarget.wholeImport}
        busy={deleting} onClose={() => setDeleteTarget(null)} onConfirm={() => void confirmDelete()} />}
    </div>
  );
}
